import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { METRICS, getMetrics } from '@/models/metrics.enum';
import {
  baseQueryParamMapping,
  defaultReportingPeriod,
} from '@/components/core/analytics/queryParamMapping';
import { searchBrandMediaTypes, pinterestPostTypes } from '@/app/library/constants';
import { enumTypes } from '@/app/pinterest/constants';
import { computed } from 'vue';
import { usePlatformStore } from '@/stores/platform';

export const useYourPosts = () => {
  const metrics = getMetrics('yourPosts');
  const platformStore = usePlatformStore();
  const showPinBoardPrivacyFilter = computed(
    () => platformStore.pinterestAccount?.importProtectedPins,
  );

  const channelFilterHandler = (scopedFilters, channelFilters) => {
    if (scopedFilters.value.pinBoardPrivacy !== enumTypes.ALL) {
      channelFilters.pinBoardPrivacy = scopedFilters.value.pinBoardPrivacy;
    }
  };

  return {
    title: 'Pinterest Posts',
    channelFilterKey: 'pinterest',
    filters: {
      brandMediaTypes: [searchBrandMediaTypes.PINTEREST_OWNED],
      default: {
        contentTags: [],
        contentTagMatchType: 'ANY',
        postType: [],
        pinBoardPrivacy: enumTypes.PUBLIC.toLowerCase(),
        reportingPeriod: defaultReportingPeriod,
        sort: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
      options: {
        postType: [
          { label: 'Photo', value: pinterestPostTypes.IMAGE },
          { label: 'Video', value: pinterestPostTypes.VIDEO },
          { label: 'Carousel', value: pinterestPostTypes.CAROUSEL },
        ],
        pinBoardPrivacy: [
          { label: 'All Pins', value: enumTypes.ALL },
          { label: 'Created Pins', value: enumTypes.PUBLIC.toLowerCase() },
          { label: 'Protected Pins', value: enumTypes.PROTECTED.toLowerCase() },
        ],
      },
      queryParamMapping: {
        ...baseQueryParamMapping,
        postType: { queryParamName: 'postType', type: 'array', elementType: 'string' },
        pinBoardPrivacy: { queryParamName: 'pinBoardPrivacy', type: 'string' },
      },
      scope: 'pinterestYourPosts',
      sortDetails: {
        options: [
          { label: 'Recently Published', value: 'DATE' },
          {
            title: 'Organic',
            children: metrics.all([
              METRICS.PINTEREST.ENGAGEMENT_RATE,
              METRICS.PINTEREST.IMPRESSIONS,
              METRICS.PINTEREST.OUTBOUND_CLICKS,
              METRICS.PINTEREST.SAVES,
              METRICS.PINTEREST.PIN_CLICKS,
              METRICS.PINTEREST.VIDEO_VIEWS,
              METRICS.PINTEREST.AVERAGE_VIDEO_WATCH_TIME,
            ]),
          },
        ],
        default: {
          sortOrder: 'DESC',
          selected: { value: 'DATE', label: 'Recently Published' },
        },
      },
    },
    download: {
      csv: {
        fileName: 'yourPinsCSV',
        type: 'pinterestYourPins',
        label: 'Your Pins CSV',
        pendingKey: 'pinterestYourPinsCSVPending',
      },
      pdf: {
        fileName: 'yourPinsPDF',
        type: 'yourPins',
        label: 'Your Pins PDF',
        pendingKey: 'yourPinsReportPending',
      },
    },
    displayMetrics: metrics.all([
      METRICS.PINTEREST.ENGAGEMENT_RATE,
      METRICS.PINTEREST.IMPRESSIONS,
      METRICS.PINTEREST.OUTBOUND_CLICKS,
      METRICS.PINTEREST.SAVES,
    ]),
    metricLabels: metrics.all(Object.values(METRICS.PINTEREST)),
    platformConnection: PLATFORM_CONNECTION.PINTEREST,
    showPinBoardPrivacyFilter,
    channelFilterHandler,
  };
};
